import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import { calendarAPI, contractsAPI, vehiclesAPI } from "../lib/api";
import { formatVehicleLabel } from "../lib/formatters";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  LayoutGrid,
  MapPin,
  Clock,
  Users,
  DollarSign,
  Circle,
  Car,
  CarFront,
  Truck,
  Wrench,
  Ban,
  Filter,
  X,
  Bus
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

export default function Calendario() {
  const { user, isLogistica } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [showBlocks, setShowBlocks] = useState(true);

  // Day services sheet (Fase 1)
  const [daySheetOpen, setDaySheetOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);

  // Fase 2.A — Granularity toggle (persisted per user via localStorage)
  const granularityKey = `calendar_granularity_${user?.id || "anon"}`;
  const [granularity, setGranularity] = useState(() => {
    try {
      return localStorage.getItem(granularityKey) || "contract";
    } catch {
      return "contract";
    }
  });
  useEffect(() => {
    try { localStorage.setItem(granularityKey, granularity); } catch (e) { /* ignore */ }
  }, [granularity, granularityKey]);

  const trafficLightColors = {
    rojo: { bg: "bg-red-500", text: "text-white", light: "bg-red-100 text-red-800" },
    amarillo: { bg: "bg-yellow-500", text: "text-black", light: "bg-yellow-100 text-yellow-800" },
    verde: { bg: "bg-green-500", text: "text-white", light: "bg-green-100 text-green-800" },
    blocked: { bg: "bg-orange-500", text: "text-white", light: "bg-orange-100 text-orange-800" }
  };

  const statusLabels = {
    solicitud: "Solicitud",
    cotizada: "Cotizada",
    contrato: "Contrato",
    asignada: "Asignada",
    en_curso: "En Curso",
    completado: "Completado",
    cancelado: "Cancelado",
    no_show: "No Show",
    blocked: "Bloqueado (Mantenimiento)"
  };

  const canSeePrices = user?.role !== "logistica" && user?.role !== "mantenimiento";

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [currentDate, view]);

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesAPI.getAll();
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (view === "month") {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else if (view === "week") {
        const dayOfWeek = currentDate.getDay();
        startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - dayOfWeek);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      } else {
        startDate = new Date(currentDate);
        endDate = new Date(currentDate);
      }
      
      const response = await calendarAPI.getEvents(
        startDate.toISOString().slice(0, 10),
        endDate.toISOString().slice(0, 10) + "T23:59:59"
      );
      setEvents(response.data);
    } catch (error) {
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  // Filter events based on unit filter and showBlocks
  const filteredEvents = useMemo(() => {
    let filtered = events;
    
    // Filter by unit if selected
    if (unitFilter) {
      filtered = filtered.filter(event => {
        // For maintenance blocks, check unit_id
        if (event.type === "maintenance_block") {
          return event.unit_id === unitFilter;
        }
        // For contracts, check vehicle_id and vehicle_lines
        if (event.vehicle_id === unitFilter) return true;
        if (Array.isArray(event.vehicle_lines)) {
          return event.vehicle_lines.some(vl => vl.assigned_vehicle_id === unitFilter);
        }
        return false;
      });
    }
    
    // Filter out blocks if not showing them
    if (!showBlocks) {
      filtered = filtered.filter(event => event.type !== "maintenance_block");
    }
    
    return filtered;
  }, [events, unitFilter, showBlocks]);

  // Fase 2.A — Lifecycle status of a unit-event based on now vs pickup/end
  const getLifecycleStatus = (pickupISO, endISO) => {
    if (!pickupISO) return null;
    const now = new Date();
    const pickup = new Date(pickupISO);
    const end = endISO ? new Date(endISO) : null;
    if (end && now > end) return "released";       // Liberado
    if (now >= pickup && (!end || now <= end)) return "in_progress"; // En curso
    return null;                                    // Programado (sin badge)
  };

  // v4.59.2: backend ya emite eventos type="operational_line" (1 por pieza). Mantenemos
  // compatibilidad con type="contract" para eventos antiguos en cache si los hubiera.
  const isContractLike = (ev) => ev?.type === "operational_line" || ev?.type === "contract";

  // Fase 2.A — Expand a contract event into N unit-level sub-events (LEGACY fallback only)
  const expandContractToUnits = (event) => {
    const lines = Array.isArray(event.vehicle_lines) ? event.vehicle_lines : [];
    if (lines.length === 0) {
      // Legacy single-unit fallback: 1 sub-event using event.vehicle_id
      return [{
        ...event,
        unit_event_key: `${event.id}__legacy`,
        unit_line_id: null,
        unit_vehicle_id: event.vehicle_id || null,
        unit_vehicle_type: null,
        unit_index: 1,
        unit_total: 1,
      }];
    }
    // Expand: handle both formats
    //  - new: [{ id, type, base_price, assigned_vehicle_id }]   → 1 line = 1 unit
    //  - legacy: [{ type, quantity: N, capacity }]              → 1 line = N units
    const flat = [];
    for (const line of lines) {
      const qty = line.id ? 1 : (line.quantity || 1);
      for (let q = 0; q < qty; q++) {
        flat.push({
          line,
          q_index: q,
        });
      }
    }
    const total = flat.length;
    return flat.map((entry, idx) => {
      const vl = entry.line;
      const lineId = vl.id || null;
      return {
        ...event,
        unit_event_key: lineId
          ? `${event.id}__${lineId}`
          : `${event.id}__${vl.type || "u"}_${entry.q_index}_${idx}`,
        unit_line_id: lineId,
        unit_vehicle_id: vl.assigned_vehicle_id || null,
        unit_vehicle_type: vl.type || null,
        unit_capacity: vl.capacity || null,
        unit_index: idx + 1,
        unit_total: total,
      };
    });
  };

  // Fase 2.A — Per-unit events (only when granularity=unit)
  const unitFilteredEvents = useMemo(() => {
    if (granularity !== "unit") return filteredEvents;
    const out = [];
    for (const ev of filteredEvents) {
      // v4.59.2: si ya viene desglosado del backend (operational_line), úsalo tal cual.
      if (ev.type === "operational_line") {
        if (unitFilter && ev.assigned_vehicle_id !== unitFilter && ev.vehicle_id !== unitFilter) continue;
        out.push({
          ...ev,
          unit_event_key: ev.id,
          unit_line_id: ev.vehicle_line_id,
          unit_vehicle_id: ev.assigned_vehicle_id || null,
          unit_vehicle_type: ev.vehicle_type || null,
        });
      } else if (ev.type === "contract") {
        // Legacy: expandir cliente-side (no debería ocurrir post-v4.59.2)
        for (const sub of expandContractToUnits(ev)) {
          if (unitFilter && sub.unit_vehicle_id !== unitFilter) continue;
          out.push(sub);
        }
      } else {
        out.push(ev);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEvents, granularity, unitFilter]);

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setDate(newDate.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSheetOpen(true);
  };

  // Fase 1: open a sheet listing ALL services for a given day
  const openDaySheet = (date, dayEvents) => {
    if (!dayEvents || dayEvents.length === 0) return;
    setSelectedDay(date);
    setSelectedDayEvents(dayEvents);
    setDaySheetOpen(true);
  };

  const handleDayEventClick = (event) => {
    // Close day sheet and open the existing event detail sheet
    setDaySheetOpen(false);
    setTimeout(() => handleEventClick(event), 150);
  };

  // Fase 1.5: lookup vehicle_type per vehicle_id for icon rendering
  const vehiclesById = useMemo(() => {
    const m = {};
    for (const v of vehicles) m[v.id] = v;
    return m;
  }, [vehicles]);

  // Map vehicle_type → icon component + aria label
  const getUnitIcon = (vehicleType) => {
    const t = (vehicleType || "").toLowerCase();
    if (t.includes("bus") || t === "autobus" || t === "autobús") return { Icon: Bus, label: "Autobús" };
    if (t.includes("sprinter") || t.includes("van")) return { Icon: Truck, label: "Sprinter/Van" };
    if (t.includes("suv")) return { Icon: CarFront, label: "SUV" };
    if (t.includes("sedan") || t.includes("sedán")) return { Icon: Car, label: "Sedán" };
    return { Icon: Car, label: "Unidad" };
  };

  const getEventUnitIcon = (event) => {
    if (!isContractLike(event)) return null;
    // Fase 2.A: en vista por unidad usamos el tipo del vehicle_line
    if (event.unit_event_key) {
      // Prefer plate-derived type if vehicle is assigned, fallback to line type
      const veh = event.unit_vehicle_id ? vehiclesById[event.unit_vehicle_id] : null;
      const t = veh?.vehicle_type || event.unit_vehicle_type;
      if (t) return getUnitIcon(t);
      return getUnitIcon(null);
    }
    const veh = event.vehicle_id ? vehiclesById[event.vehicle_id] : null;
    if (!veh) return null;
    return getUnitIcon(veh.vehicle_type);
  };

  // Fase 2.A — unit-event display label (plate or fallback type)
  const getUnitDisplayLabel = (event) => {
    if (!event.unit_event_key) return null;
    if (event.unit_vehicle_id) {
      const veh = vehiclesById[event.unit_vehicle_id];
      if (veh) return formatVehicleLabel(veh, { showPlate: false });
    }
    // Sin asignar
    const typeLabel = (event.unit_vehicle_type || "Unidad").toString();
    return typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];
    
    // Previous month days
    for (let i = startPadding - 1; i >= 0; i--) {
      const day = new Date(year, month, -i);
      days.push({ date: day, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().slice(0, 10);
    return unitFilteredEvents.filter(event => {
      // v4.64.0: todos los eventos (incluidos servicios multi-día) se muestran en cada día
      // dentro de su rango [inicio, fin], no solo en el día de inicio.
      const eventStart = event.start?.slice(0, 10);
      const eventEnd = (event.end || event.start)?.slice(0, 10);
      if (!eventStart) return false;
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const formatTime = (dateString) => {
    if (!dateString || dateString.length <= 10) return "Todo el día";
    return new Date(dateString).toLocaleTimeString("es-MX", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const days = getDaysInMonth();
  const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

  const blockCount = events.filter(e => e.type === "maintenance_block").length;

  // Fase 2.A — Render helper for day-service items (used in both grouped and flat layouts)
  const renderDayServiceItem = (event) => {
    const unitIconInfo = getEventUnitIcon(event);
    const UnitIcon = unitIconInfo?.Icon;
    const isBlock = event.type === "maintenance_block";
    const isFrame = event.type === "contract_frame";
    const isUnitView = !!event.unit_event_key;
    const clientName = event.title?.split(" - ")[0] || "—";
    const serviceDesc = event.title?.split(" - ").slice(1).join(" - ") || "";
    const unitLabel = isUnitView ? getUnitDisplayLabel(event) : null;
    const unitUnassigned = isUnitView && !event.unit_vehicle_id;
    const lifecycle = isUnitView ? getLifecycleStatus(event.pickup_datetime, event.end_datetime) : null;
    const itemKey = event.unit_event_key || event.id;
    return (
      <button
        key={itemKey}
        type="button"
        onClick={() => handleDayEventClick(event)}
        className={cn(
          "w-full text-left rounded-lg border p-3 transition-colors hover:bg-slate-50",
          isBlock && "border-orange-200 bg-orange-50/40 hover:bg-orange-50",
          unitUnassigned && "border-dashed"
        )}
        data-testid={`day-service-item-${itemKey}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn(
              "w-2 h-2 rounded-full mt-2 flex-shrink-0",
              isBlock
                ? "bg-orange-500"
                : trafficLightColors[event.traffic_light]?.bg
            )} />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {isBlock ? (
                  <>
                    <Wrench className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <span className="font-medium text-slate-900">Bloqueo: {event.unit_plate}</span>
                  </>
                ) : (
                  <>
                    {UnitIcon && (
                      <UnitIcon className="w-4 h-4 text-slate-600 flex-shrink-0" aria-label={unitIconInfo.label} />
                    )}
                    {isUnitView ? (
                      <>
                        <span className="font-mono font-medium text-slate-900">{unitLabel}</span>
                        <span className="text-xs text-slate-500">·</span>
                        <span className="text-sm text-slate-700 truncate">{clientName}</span>
                        {event.unit_total > 1 && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] text-slate-500">
                            {event.unit_index}/{event.unit_total}
                          </Badge>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-slate-900 truncate">{clientName}</span>
                        {event.required_units > 1 && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                            <Bus className="w-3 h-3 mr-0.5" />
                            {event.required_units}
                          </Badge>
                        )}
                      </>
                    )}
                    {isFrame && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-purple-300 text-purple-700">Marco</Badge>
                    )}
                    {/* v4.59.2: badge por window_source de la pieza operativa */}
                    {event.window_source === "line" && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-amber-400 text-amber-700" title="Ventana personalizada por línea">Personalizada</Badge>
                    )}
                    {event.window_source === "v2" && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-emerald-400 text-emerald-700" title="Asignación v2 confirmada">v2</Badge>
                    )}
                  </>
                )}
              </div>

              {serviceDesc && !isBlock && !isUnitView && (
                <p className="text-xs text-slate-500 mt-0.5 truncate">{serviceDesc}</p>
              )}

              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-600 flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {event.allDay || isBlock
                    ? "Todo el día"
                    : isUnitView && event.end_datetime
                      ? `${formatTime(event.pickup_datetime)} – ${formatTime(event.end_datetime)}`
                      : formatTime(event.start)}
                </span>
                {!isBlock && event.origin && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{event.origin}</span>
                  </span>
                )}
                {isBlock && event.reason && (
                  <span className="truncate text-orange-700">{event.reason}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {isUnitView && unitUnassigned && (
              <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid="badge-unassigned">Sin asignar</Badge>
            )}
            {isUnitView && lifecycle === "in_progress" && (
              <Badge className="bg-blue-100 text-blue-800 text-[10px]" data-testid="badge-in-progress">En curso</Badge>
            )}
            {isUnitView && lifecycle === "released" && (
              <Badge className="bg-slate-100 text-slate-600 text-[10px]" data-testid="badge-released">Liberado</Badge>
            )}
            <Badge
              className={cn(
                "text-[10px]",
                isBlock
                  ? "bg-orange-100 text-orange-800"
                  : trafficLightColors[event.traffic_light]?.light
              )}
            >
              {isBlock ? "BLOQUEADO" : statusLabels[event.status] || event.status}
            </Badge>
          </div>
        </div>
      </button>
    );
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="calendario-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Calendario</h1>
            <p className="text-slate-500 mt-1">Vista de servicios programados y bloqueos</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Fase 2.A — Granularity toggle (persisted per user) */}
            <Tabs value={granularity} onValueChange={setGranularity}>
              <TabsList data-testid="granularity-tabs">
                <TabsTrigger value="contract" data-testid="granularity-contract">Por contrato</TabsTrigger>
                <TabsTrigger value="unit" data-testid="granularity-unit">Por unidad</TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs value={view} onValueChange={setView} className="hidden sm:block">
              <TabsList>
                <TabsTrigger value="month" data-testid="view-month">Mes</TabsTrigger>
                <TabsTrigger value="week" data-testid="view-week">Semana</TabsTrigger>
                <TabsTrigger value="day" data-testid="view-day">Día</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Calendar Controls */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => navigateDate(-1)} data-testid="prev-btn">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => navigateDate(1)} data-testid="next-btn">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" onClick={goToToday} data-testid="today-btn">
                    Hoy
                  </Button>
                </div>
                
                <h2 className="text-xl font-heading font-semibold">
                  {currentDate.toLocaleDateString("es-MX", {
                    month: "long",
                    year: "numeric",
                    ...(view === "day" && { day: "numeric" })
                  })}
                </h2>
                
                <div className="flex items-center gap-4">
                  <div className="hidden sm:flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      Sin asignar
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      Pendiente
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      Confirmado
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <Wrench className="w-3 h-3" />
                      Bloqueado
                    </span>
                  </div>
                </div>
              </div>

              {/* Filters Row */}
              <div className="flex items-center gap-4 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">Filtrar:</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select value={unitFilter || "all"} onValueChange={(v) => setUnitFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]" data-testid="unit-filter">
                      <SelectValue placeholder="Todas las unidades" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las unidades</SelectItem>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          <span className="flex items-center gap-2">
                            <Car className="w-3 h-3" />
                            {formatVehicleLabel(v)}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {unitFilter && (
                    <Button variant="ghost" size="sm" onClick={() => setUnitFilter("")}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showBlocks}
                    onChange={(e) => setShowBlocks(e.target.checked)}
                    className="rounded border-slate-300"
                    data-testid="show-blocks-checkbox"
                  />
                  <span className="flex items-center gap-1">
                    <Ban className="w-3 h-3 text-orange-500" />
                    Mostrar bloqueos ({blockCount})
                  </span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Week Day Headers */}
                <div className="grid grid-cols-7 border-b bg-slate-50">
                  {weekDays.map((day) => (
                    <div key={day} className="p-3 text-center text-sm font-medium text-slate-600 border-r last:border-r-0">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7">
                  {days.map((day, index) => {
                    const dayEvents = getEventsForDay(day.date);
                    const isToday = day.date.toDateString() === new Date().toDateString();
                    const hasBlocks = dayEvents.some(e => e.type === "maintenance_block");
                    
                    return (
                      <div
                        key={index}
                        className={cn(
                          "min-h-[120px] border-r border-b last:border-r-0 p-2 transition-colors hover:bg-slate-50",
                          !day.isCurrentMonth && "bg-slate-50/50",
                          hasBlocks && "bg-orange-50/30"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => openDaySheet(day.date, dayEvents)}
                          disabled={dayEvents.length === 0}
                          className={cn(
                            "w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1 transition-colors",
                            isToday && "bg-blue-600 text-white",
                            !isToday && !day.isCurrentMonth && "text-slate-400",
                            !isToday && day.isCurrentMonth && "text-slate-700",
                            dayEvents.length > 0 && "hover:bg-slate-200 cursor-pointer",
                            dayEvents.length === 0 && "cursor-default"
                          )}
                          data-testid={`day-number-${day.date.toISOString().slice(0,10)}`}
                          aria-label={dayEvents.length > 0 ? `Ver ${dayEvents.length} servicios del día` : undefined}
                        >
                          {day.date.getDate()}
                        </button>
                        
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((event) => {
                            const unitIconInfo = getEventUnitIcon(event);
                            const UnitIcon = unitIconInfo?.Icon;
                            const isUnitView = !!event.unit_event_key;
                            const unitLabel = isUnitView ? getUnitDisplayLabel(event) : null;
                            const unitUnassigned = isUnitView && !event.unit_vehicle_id;
                            const lifecycle = isUnitView ? getLifecycleStatus(event.pickup_datetime, event.end_datetime) : null;
                            // v4.64.0: marcar días de continuación de servicios multi-día
                            const _dayStr = day.date.toISOString().slice(0, 10);
                            const _evStart = event.start?.slice(0, 10);
                            const _evEnd = (event.end || event.start)?.slice(0, 10);
                            const isContinuation = !!(_evStart && _evEnd && _evEnd > _evStart && _dayStr > _evStart);
                            return (
                            <button
                              key={event.unit_event_key || event.id}
                              onClick={() => handleEventClick(event)}
                              className={cn(
                                "w-full text-left rounded px-2 py-1 text-xs font-medium truncate transition-transform hover:scale-[1.02]",
                                event.type === "maintenance_block" 
                                  ? "bg-orange-500 text-white border-l-4 border-orange-700"
                                  : cn(
                                      trafficLightColors[event.traffic_light]?.bg,
                                      trafficLightColors[event.traffic_light]?.text
                                    ),
                                unitUnassigned && "opacity-80 ring-1 ring-white/40"
                              )}
                              data-testid={`event-${event.unit_event_key || event.id}`}
                            >
                              {event.type === "maintenance_block" ? (
                                <span className="flex items-center gap-1">
                                  <Wrench className="w-3 h-3" />
                                  {event.unit_plate}
                                </span>
                              ) : (
                                <span className="flex items-center justify-between gap-1">
                                  <span className="flex items-center gap-1 truncate">
                                    {UnitIcon && (
                                      <UnitIcon className="w-3 h-3 flex-shrink-0" aria-label={unitIconInfo.label} />
                                    )}
                                    <span className="truncate">
                                      {isContinuation && (
                                        <span className="opacity-70 mr-0.5" title="Servicio continúa de un día anterior">» continúa · </span>
                                      )}
                                      {isUnitView ? (
                                        <>
                                          <span className="font-mono">{unitLabel}</span>
                                          <span className="opacity-80"> · {event.title?.split(" - ")[0]}</span>
                                        </>
                                      ) : (
                                        <>
                                          {event.allDay ? "" : `${formatTime(event.start)} - `}
                                          {event.title?.split(" - ")[0]}
                                        </>
                                      )}
                                    </span>
                                  </span>
                                  {isUnitView ? (
                                    <span className="flex items-center gap-0.5 flex-shrink-0">
                                      {unitUnassigned && (
                                        <span className="bg-white/30 rounded px-1 text-[9px]">N/A</span>
                                      )}
                                      {lifecycle === "in_progress" && (
                                        <span className="bg-white/30 rounded px-1 text-[9px]">●</span>
                                      )}
                                      {lifecycle === "released" && (
                                        <span className="bg-white/20 rounded px-1 text-[9px] line-through">✓</span>
                                      )}
                                    </span>
                                  ) : (
                                    event.required_units > 1 && (
                                      <span className="flex items-center gap-0.5 flex-shrink-0 bg-white/30 rounded px-1">
                                        <Bus className="w-3 h-3" />
                                        <span>{event.required_units}</span>
                                      </span>
                                    )
                                  )}
                                </span>
                              )}
                            </button>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <button
                              type="button"
                              onClick={() => openDaySheet(day.date, dayEvents)}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline px-2 font-medium cursor-pointer"
                              data-testid={`more-events-${day.date.toISOString().slice(0,10)}`}
                            >
                              +{dayEvents.length - 3} más
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Day Services Sheet (Fase 1) — lista scrollable con TODOS los servicios */}
        <Sheet open={daySheetOpen} onOpenChange={setDaySheetOpen}>
          <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="day-services-sheet">
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
                {selectedDay && selectedDay.toLocaleDateString("es-MX", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                })}
              </SheetTitle>
              <p className="text-sm text-slate-500">
                {selectedDayEvents.length} servicio{selectedDayEvents.length === 1 ? "" : "s"} del día
              </p>
            </SheetHeader>

            <div className="mt-4 flex-1 overflow-y-auto pr-1 space-y-2" data-testid="day-services-list">
              {(() => {
                // Fase 2.A — Group by vehicle type when granularity=unit
                if (granularity !== "unit") {
                  return selectedDayEvents.map((event) => renderDayServiceItem(event));
                }
                // Group items: contract sub-events grouped by type, others (blocks/frames) ungrouped at top
                const ungrouped = [];
                const groups = {}; // typeKey → { label, IconCmp, items: [] }
                for (const ev of selectedDayEvents) {
                  if (!isContractLike(ev)) {
                    ungrouped.push(ev);
                    continue;
                  }
                  const veh = ev.unit_vehicle_id ? vehiclesById[ev.unit_vehicle_id] : null;
                  const t = (veh?.vehicle_type || ev.unit_vehicle_type || "otros").toLowerCase();
                  const ic = getUnitIcon(t);
                  if (!groups[t]) groups[t] = { label: ic.label, IconCmp: ic.Icon, items: [] };
                  groups[t].items.push(ev);
                }
                const orderedKeys = Object.keys(groups).sort();
                return (
                  <>
                    {ungrouped.map((event) => renderDayServiceItem(event))}
                    {orderedKeys.map((tk) => {
                      const g = groups[tk];
                      const GIcon = g.IconCmp;
                      return (
                        <div key={`group-${tk}`} className="space-y-2" data-testid={`day-group-${tk}`}>
                          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 uppercase tracking-wide pt-2 border-b pb-1">
                            <GIcon className="w-4 h-4" />
                            <span>{g.label}</span>
                            <span className="text-slate-400 font-normal">({g.items.length})</span>
                          </div>
                          {g.items.map((event) => renderDayServiceItem(event))}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </SheetContent>
        </Sheet>

        {/* Event Detail Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full sm:max-w-lg">
            <SheetHeader>
              <SheetTitle className="font-heading">
                {selectedEvent?.type === "maintenance_block" ? "Bloqueo de Unidad" : "Detalle del Servicio"}
              </SheetTitle>
            </SheetHeader>
            
            {selectedEvent && (
              <div className="mt-6 space-y-6">
                {/* Maintenance Block Detail */}
                {selectedEvent.type === "maintenance_block" ? (
                  <>
                    {/* Block Badge */}
                    <Badge className="bg-orange-100 text-orange-800 text-sm px-3 py-1">
                      <Ban className="w-4 h-4 mr-2" />
                      BLOQUEADO (all-day)
                    </Badge>

                    {/* Unit Info */}
                    <div>
                      <p className="text-sm text-slate-500">Unidad</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Car className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-lg font-medium font-mono">{selectedEvent.unit_plate}</p>
                          <p className="text-sm text-slate-500">{selectedEvent.unit_name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="flex items-center gap-3">
                      <CalendarIcon className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="text-sm text-slate-500">Período de Bloqueo</p>
                        <p className="font-medium">
                          {selectedEvent.start} → {selectedEvent.end || selectedEvent.start}
                        </p>
                        <p className="text-xs text-slate-500">Día completo (inclusivo)</p>
                      </div>
                    </div>

                    {/* Reason */}
                    <div>
                      <p className="text-sm text-slate-500">Razón</p>
                      <p className="mt-1">{selectedEvent.reason}</p>
                    </div>

                    {/* Source */}
                    <div>
                      <p className="text-sm text-slate-500">Origen</p>
                      <Badge variant={selectedEvent.source_ref ? "secondary" : "outline"} className="mt-1">
                        {selectedEvent.source_ref ? (
                          <><Wrench className="w-3 h-3 mr-1" /> Bitácora de Mantenimiento</>
                        ) : (
                          "Bloqueo Manual"
                        )}
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t">
                      <Link to="/mantenimiento">
                        <Button className="w-full" variant="outline" data-testid="view-maintenance-btn">
                          <Wrench className="w-4 h-4 mr-2" />
                          Ir a Mantenimiento
                        </Button>
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Traffic Light Badge */}
                    <Badge className={cn(
                      "text-sm px-3 py-1",
                      trafficLightColors[selectedEvent.traffic_light]?.light
                    )}>
                      <Circle className={cn(
                        "w-2 h-2 mr-2 fill-current"
                      )} />
                      {selectedEvent.traffic_light?.toUpperCase()}
                    </Badge>

                    {/* Client */}
                    <div>
                      <p className="text-sm text-slate-500">Cliente</p>
                      <p className="text-lg font-medium">
                        {selectedEvent.title?.split(" - ")[0]}
                      </p>
                    </div>

                    {/* Units indicator */}
                    {selectedEvent.required_units > 1 && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Bus className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-slate-500">Unidades Contratadas</p>
                          <p className="font-medium text-blue-700">{selectedEvent.required_units} unidades</p>
                        </div>
                      </div>
                    )}

                    {/* Route */}
                    {selectedEvent.origin && (
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-500">Origen</p>
                            <p className="font-medium">{selectedEvent.origin}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-slate-500">Destino</p>
                            <p className="font-medium">{selectedEvent.destination}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Date/Time — v4.64.0: mostrar Inicio y Fin (ventana efectiva) */}
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-slate-500">Inicio</p>
                          <p className="font-medium" data-testid="event-detail-start">
                            {selectedEvent.allDay ? (
                              <>
                                {selectedEvent.start}
                                <span className="text-xs text-slate-500 block">Todo el día</span>
                              </>
                            ) : (
                              new Date(selectedEvent.start).toLocaleString("es-MX", {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                hour: "2-digit",
                                minute: "2-digit"
                              })
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Fin</p>
                          <p className="font-medium" data-testid="event-detail-end">
                            {selectedEvent.end
                              ? (selectedEvent.allDay
                                  ? selectedEvent.end
                                  : new Date(selectedEvent.end).toLocaleString("es-MX", {
                                      weekday: "long",
                                      day: "numeric",
                                      month: "long",
                                      hour: "2-digit",
                                      minute: "2-digit"
                                    }))
                              : "No especificado"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-sm text-slate-500">Estado</p>
                      <Badge variant="outline" className="mt-1">
                        {statusLabels[selectedEvent.status]}
                      </Badge>
                    </div>

                    {/* Price (if allowed) */}
                    {canSeePrices && selectedEvent.total_price && (
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm text-slate-500">Precio</p>
                          <p className="text-lg font-bold text-green-600">
                            ${selectedEvent.total_price.toLocaleString("es-MX")}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {selectedEvent.type !== "maintenance_block" && (
                      <div className="pt-4 border-t">
                        {selectedEvent.type === "contract_frame" ? (
                          <Link to={`/contratos-marco/${selectedEvent.id}`}>
                            <Button className="w-full" data-testid="view-contract-frame-btn">
                              Ver Contrato Marco
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/contratos/${selectedEvent.contract_id || selectedEvent.id}`}>
                            <Button className="w-full" data-testid="view-contract-btn">
                              Ver Contrato Completo
                            </Button>
                          </Link>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
